import { z } from "zod";
import { modelGatewayErrorOriginSchema, modelGatewayFailureSchema } from "./model-gateway.js";
// Provider signatures, diagnostics and extension fields must survive replay. Validate
// the fields the decoder consumes without closing provider-owned objects to extensions.
const textBlock = z.object({ type: z.literal("text"), text: z.string(), textSignature: z.string().optional() }).passthrough();
const thinkingBlock = z.object({ type: z.literal("thinking"), thinking: z.string(),
    thinkingSignature: z.string().optional(), redacted: z.boolean().optional() }).passthrough();
const toolBlock = z.object({ type: z.literal("toolCall"), id: z.string(), name: z.string(),
    arguments: z.json(), thoughtSignature: z.string().optional() }).passthrough();
const count = z.number().nonnegative();
const usage = z.object({ input: count, output: count, cacheRead: count, cacheWrite: count,
    totalTokens: count, cacheWrite1h: count.optional(), reasoning: count.optional(),
    cost: z.object({ input: count, output: count, cacheRead: count, cacheWrite: count, total: count }).passthrough(),
}).passthrough();
const message = z.object({
    role: z.literal("assistant"), content: z.array(z.discriminatedUnion("type", [textBlock, thinkingBlock, toolBlock])),
    // Usage is absent where the provider reports none: the Codex subscription's responses omit token usage,
    // and the gateway keeps it unavailable rather than a false zero (openai_codex_auth.ts, preserveUnknownCodexUsage).
    api: z.string(), provider: z.string(), model: z.string(), usage: usage.optional(),
    timestamp: z.number(), stopReason: z.enum(["stop", "length", "toolUse", "error", "aborted"]),
    errorMessage: z.string().optional(), responseId: z.string().nullish().transform((value) => value ?? undefined), responseModel: z.string().optional(),
    diagnostics: z.array(z.object({ type: z.string(), timestamp: z.number(),
        error: z.object({ name: z.string().optional(), message: z.string(), stack: z.string().optional(),
            code: z.union([z.string(), z.number()]).optional() }).passthrough().optional(),
        details: z.record(z.string(), z.unknown()).optional(),
    }).passthrough()).optional(),
}).passthrough();
const contentIndex = z.number().int().nonnegative();
// A newer gateway may attribute failures to a source this executor cannot name.
// Keep the error frame, without misattributing it to a known source.
const wireFailure = modelGatewayFailureSchema.extend({
    origin: z.string().min(1).transform((origin) => {
        const parsed = modelGatewayErrorOriginSchema.safeParse(origin);
        return parsed.success ? parsed.data : "unknown";
    }),
}).passthrough();
export const modelGatewayCompactEventSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("start"), partial: message }),
    z.object({ type: z.literal("done"), reason: z.enum(["stop", "length", "toolUse"]), message })
        .refine((event) => event.reason === event.message.stopReason, { message: "Terminal reason disagrees with message" }),
    z.object({ type: z.literal("error"), reason: z.enum(["error", "aborted"]), error: message,
        failure: wireFailure.optional() })
        .refine((event) => event.reason === event.error.stopReason, { message: "Terminal reason disagrees with message" }),
    z.object({ type: z.literal("text_start"), contentIndex, block: textBlock }),
    z.object({ type: z.literal("thinking_start"), contentIndex, block: thinkingBlock }),
    z.object({ type: z.literal("toolcall_start"), contentIndex, block: toolBlock }),
    z.object({ type: z.literal("text_delta"), contentIndex, delta: z.string() }),
    z.object({ type: z.literal("thinking_delta"), contentIndex, delta: z.string() }),
    z.object({ type: z.literal("toolcall_delta"), contentIndex, delta: z.string() }),
    z.object({ type: z.literal("text_end"), contentIndex, content: z.string(), block: textBlock }),
    z.object({ type: z.literal("thinking_end"), contentIndex, content: z.string(), block: thinkingBlock }),
    z.object({ type: z.literal("toolcall_end"), contentIndex, toolCall: toolBlock }),
]);
export const modelGatewayStreamSideEventSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("mlx.payload"), payload: z.unknown().refine((value) => value !== undefined, { message: "Payload is required" }) }),
    z.object({ type: z.literal("mlx.receipt"), receiptId: z.string().min(1) }),
    z.object({ type: z.literal("mlx.keepalive") }),
    z.object({ type: z.literal("mlx.venus_observation"), kind: z.string(), payload: z.record(z.string(), z.unknown()) }),
    z.object({ type: z.literal("mlx.venus_stream_progress"), progress: z.object({
            outputTokens: count, inputTokens: count.optional(), totalTokens: count.optional(), decodeTokensPerSecond: count.optional(),
        }).passthrough() }),
    z.object({ type: z.literal("mlx.venus_runtime_progress"), progress: z.object({
            stage: z.string().min(1), auditId: z.string(), createdAt: z.number(),
            jobId: z.string().optional(), providerState: z.string().optional(), diagnosticCode: z.string().optional(),
            detail: z.string().optional(), elapsedMs: count.optional(), stageOneMs: count.optional(), stageTwoMs: count.optional(),
        }).passthrough() }),
]);
const knownSideTypes = new Set(modelGatewayStreamSideEventSchema.options.map((schema) => schema.shape.type.value));
// A newer gateway can add advisory side events. Known events still have to pass
// their schema; an invalid known frame must never fall through as an extension.
const ignoredSideEvent = z.object({
    type: z.string().startsWith("mlx.").refine((type) => !knownSideTypes.has(type)),
}).transform(() => ({ type: "mlx.ignored" }));
/** Validate consumed fields before state changes; ignore unknown advisory events. */
export const modelGatewayWireEventSchema = z.union([
    modelGatewayCompactEventSchema, modelGatewayStreamSideEventSchema, ignoredSideEvent,
]);
